import type { Scenario } from '../types/volunteer';
import { SAMPLE_MESSAGE } from './program';

export const scenarios: Scenario[] = [
  {
    id: 'connect-off',
    group: 'connect',
    outputs: '1',
    title: 'Discount off',
    description: 'Default for every program. Dependent fields stay hidden.',
    preset: {
      view: 'connect',
      config: {
        discountEnabled: false,
        message: '',
        roles: [
          { id: 'head', label: 'Head Coach', opportunities: 0, claimed: 0 },
          { id: 'assistant', label: 'Assistant Coach', opportunities: 0, claimed: 0 },
          { id: 'helper', label: 'Helper', opportunities: 0, claimed: 0 },
        ],
      },
      unassignedClaims: 0,
      otherReservations: 0,
    },
  },
  {
    id: 'connect-empty',
    group: 'connect',
    outputs: '2',
    title: 'Enabled · empty',
    description: 'Just turned on. No opportunities yet, so customers see nothing.',
    preset: {
      view: 'connect',
      config: {
        discountEnabled: true,
        amount: 50,
        message: '',
        roles: [
          { id: 'head', label: 'Head Coach', opportunities: 0, claimed: 0 },
          { id: 'assistant', label: 'Assistant Coach', opportunities: 0, claimed: 0 },
          { id: 'helper', label: 'Helper', opportunities: 0, claimed: 0 },
        ],
      },
      unassignedClaims: 0,
      otherReservations: 0,
    },
  },
  {
    id: 'connect-on',
    group: 'connect',
    outputs: '2',
    title: 'Enabled · configured',
    description: '$50 program amount with opportunities set for each role.',
    preset: {
      view: 'connect',
      config: {
        discountEnabled: true,
        amount: 50,
        message: '',
        roles: [
          { id: 'head', label: 'Head Coach', opportunities: 4, claimed: 0 },
          { id: 'assistant', label: 'Assistant Coach', opportunities: 4, claimed: 0 },
          { id: 'helper', label: 'Helper', opportunities: 2, claimed: 0 },
        ],
      },
      unassignedClaims: 0,
      otherReservations: 0,
    },
  },
  {
    id: 'connect-capacity',
    group: 'connect',
    outputs: '3',
    title: 'Claimed & remaining',
    description: 'Mid-season: one role exhausted, live holds in carts.',
    preset: {
      view: 'connect',
      config: {
        roles: [
          { id: 'head', label: 'Head Coach', opportunities: 4, claimed: 2 },
          { id: 'assistant', label: 'Assistant Coach', opportunities: 4, claimed: 4 },
          { id: 'helper', label: 'Helper', opportunities: 2, claimed: 1 },
        ],
      },
      unassignedClaims: 1,
      otherReservations: 1,
    },
  },
  {
    id: 'connect-exhausted',
    group: 'connect',
    outputs: '3',
    title: 'Capacity exhausted',
    description: 'Every opportunity claimed. Coach Credit takes over.',
    preset: {
      view: 'connect',
      config: {
        coachCreditEnabled: true,
        roles: [
          { id: 'head', label: 'Head Coach', opportunities: 4, claimed: 4 },
          { id: 'assistant', label: 'Assistant Coach', opportunities: 4, claimed: 4 },
          { id: 'helper', label: 'Helper', opportunities: 2, claimed: 2 },
        ],
      },
      unassignedClaims: 0,
      otherReservations: 0,
    },
  },
  {
    id: 'connect-message',
    group: 'connect',
    outputs: '4',
    title: 'Message & preview',
    description: 'Custom message with the live customer preview.',
    preset: { view: 'connect', config: { message: SAMPLE_MESSAGE } },
  },
  {
    id: 'cust-discount',
    group: 'mobile',
    outputs: '5',
    state: 'B',
    title: 'Discount offer',
    description: 'Discount on, capacity available, no custom message.',
    preset: { view: 'customer', device: 'mobile', config: { message: '' } },
  },
  {
    id: 'cust-discount-msg',
    group: 'mobile',
    outputs: '5',
    state: 'B',
    title: 'Discount + custom message',
    description: 'Franchise message shown below the system offer.',
    preset: { view: 'customer', device: 'mobile' },
  },
  {
    id: 'cust-both',
    group: 'mobile',
    outputs: '5',
    state: 'D',
    title: 'Both enabled · capacity left',
    description: 'Same screen as B. Coach Credit is suppressed.',
    preset: { view: 'customer', device: 'mobile', config: { coachCreditEnabled: true } },
  },
  {
    id: 'cust-coach',
    group: 'mobile',
    outputs: '6',
    state: 'C',
    title: 'Automated Coach Credit',
    description: 'Discount off, coach credit on.',
    preset: {
      view: 'customer',
      device: 'mobile',
      config: { discountEnabled: false, coachCreditEnabled: true },
    },
  },
  {
    id: 'cust-standard',
    group: 'mobile',
    outputs: '7',
    state: 'A',
    title: 'Standard messaging',
    description: 'Neither incentive enabled.',
    preset: {
      view: 'customer',
      device: 'mobile',
      config: { discountEnabled: false, coachCreditEnabled: false },
    },
  },
  {
    id: 'cust-exhausted-standard',
    group: 'mobile',
    outputs: '7',
    state: 'A',
    title: 'Exhausted · no coach credit',
    description: 'Discount on but used up, Coach Credit off → standard.',
    preset: {
      view: 'customer',
      device: 'mobile',
      config: {
        coachCreditEnabled: false,
        roles: [
          { id: 'head', label: 'Head Coach', opportunities: 4, claimed: 4 },
          { id: 'assistant', label: 'Assistant Coach', opportunities: 4, claimed: 4 },
          { id: 'helper', label: 'Helper', opportunities: 2, claimed: 2 },
        ],
      },
      unassignedClaims: 0,
      otherReservations: 0,
    },
  },
  {
    id: 'cust-someone',
    group: 'mobile',
    outputs: '8',
    title: 'Someone Else details',
    description: 'Eligible option with volunteer info capture.',
    preset: {
      view: 'customer',
      device: 'mobile',
      choice: 'someone',
      hold: 'reserved',
      someone: { firstName: 'Jordan', lastName: 'Rivera', relationship: 'Relative' },
    },
  },
  {
    id: 'cart-applied',
    group: 'mobile',
    outputs: '9',
    state: 'F',
    title: 'Cart · discount applied',
    description: 'Distinct line item alongside other credits.',
    preset: { view: 'customer', device: 'mobile', step: 'cart', choice: 'myself', hold: 'reserved' },
  },
  {
    id: 'cart-removed',
    group: 'mobile',
    outputs: '10',
    state: 'G',
    title: 'Cart · discount removed',
    description: 'Changed to Maybe; hold released, total recalculated.',
    preset: { view: 'customer', device: 'mobile', step: 'cart', choice: 'maybe', notice: 'removed' },
  },
  {
    id: 'cust-both-exhausted',
    group: 'mobile',
    outputs: '11',
    state: 'E',
    title: 'Exhausted · coach credit',
    description: 'Both on, no discount opportunities left.',
    preset: {
      view: 'customer',
      device: 'mobile',
      config: {
        coachCreditEnabled: true,
        roles: [
          { id: 'head', label: 'Head Coach', opportunities: 4, claimed: 4 },
          { id: 'assistant', label: 'Assistant Coach', opportunities: 4, claimed: 4 },
          { id: 'helper', label: 'Helper', opportunities: 2, claimed: 2 },
        ],
      },
      unassignedClaims: 0,
      otherReservations: 0,
    },
  },
  {
    id: 'cust-unavailable',
    group: 'mobile',
    outputs: '11',
    state: 'H',
    title: 'Gone before reserved',
    description: 'Last opportunity taken between viewing and selecting.',
    preset: {
      view: 'customer',
      device: 'mobile',
      config: {
        coachCreditEnabled: true,
        roles: [
          { id: 'head', label: 'Head Coach', opportunities: 4, claimed: 4 },
          { id: 'assistant', label: 'Assistant Coach', opportunities: 4, claimed: 4 },
          { id: 'helper', label: 'Helper', opportunities: 2, claimed: 1 },
        ],
      },
      unassignedClaims: 0,
      otherReservations: 1,
      choice: 'myself',
      notice: 'unavailable',
      pageOffer: 'coachCredit',
    },
  },
  {
    id: 'cart-expired',
    group: 'mobile',
    outputs: '11',
    title: 'Reservation expired',
    description: 'Hold timed out; customer must revalidate.',
    preset: {
      view: 'customer',
      device: 'mobile',
      step: 'cart',
      choice: 'myself',
      hold: 'expired',
      notice: 'expired',
    },
  },
  {
    id: 'desk-standard',
    group: 'desktop',
    outputs: '12',
    state: 'A',
    title: 'Standard messaging',
    description: 'Neither incentive enabled.',
    preset: {
      view: 'customer',
      device: 'desktop',
      config: { discountEnabled: false, coachCreditEnabled: false },
    },
  },
  {
    id: 'desk-coach',
    group: 'desktop',
    outputs: '12',
    state: 'C',
    title: 'Automated Coach Credit',
    description: 'Discount off, coach credit on.',
    preset: {
      view: 'customer',
      device: 'desktop',
      config: { discountEnabled: false, coachCreditEnabled: true },
    },
  },
  {
    id: 'desk-discount',
    group: 'desktop',
    outputs: '12',
    state: 'B',
    title: 'Discount offer',
    description: 'System offer and terms only, no custom message.',
    preset: { view: 'customer', device: 'desktop', config: { message: '' } },
  },
  {
    id: 'desk-discount-msg',
    group: 'desktop',
    outputs: '12',
    state: 'B',
    title: 'Discount + custom message',
    description: 'Franchise message below the system offer.',
    preset: { view: 'customer', device: 'desktop' },
  },
  {
    id: 'desk-exhausted',
    group: 'desktop',
    outputs: '12',
    state: 'E',
    title: 'Exhausted · coach credit',
    description: 'Fallback incentive at desktop width.',
    preset: {
      view: 'customer',
      device: 'desktop',
      config: {
        coachCreditEnabled: true,
        roles: [
          { id: 'head', label: 'Head Coach', opportunities: 4, claimed: 4 },
          { id: 'assistant', label: 'Assistant Coach', opportunities: 4, claimed: 4 },
          { id: 'helper', label: 'Helper', opportunities: 2, claimed: 2 },
        ],
      },
      unassignedClaims: 0,
      otherReservations: 0,
    },
  },
  {
    id: 'desk-cart',
    group: 'desktop',
    outputs: '12',
    state: 'F',
    title: 'Cart · discount applied',
    description: 'Two-column cart with order summary.',
    preset: { view: 'customer', device: 'desktop', step: 'cart', choice: 'myself', hold: 'reserved' },
  },
  {
    id: 'desk-removed',
    group: 'desktop',
    outputs: '12',
    state: 'G',
    title: 'Cart · discount removed',
    description: 'Recalculated total after opting out.',
    preset: { view: 'customer', device: 'desktop', step: 'cart', choice: 'no', notice: 'removed' },
  },
];
